import type {
  HistoryEventRecord,
  OperatingStatus,
  SummaryRecord,
  WorldSnapshot,
} from '../../domain/types.js';
import { WORLD_DOMAIN } from '../../constants.js';
import {
  activeAbsentCharacterIds,
  pruneAbsenceHistory,
} from '../../runtime/characterAbsence.js';
import { isCharacterPresent } from '../../runtime/visitPresence.js';
import {
  ensureDailyDevices,
  resolveActiveDevices,
} from '../../runtime/deviceScheduler.js';
import { formatAquariumSubtitle } from '../aquarium/formatSubtitle.js';
import type { WorldLaunchHandoff } from '../CreateWorld/draftStorage.js';
import {
  advanceWorldClock,
  formatWorldClock,
  inWorldDateFromIso,
  inWorldWeekKeyFromDate,
  operatingStatusForClock,
  syncClockToNow,
} from './clock.js';
import {
  acknowledgeMissedEvents,
  markEventUserObserved,
  missedHistoryEvents,
  shouldShowMissedPrompt,
  tagMissedEventsAfterAbsence,
} from './observations.js';
import { isRetiredHistorySummary, loadRuntimeState, saveRuntimeState, withoutRetiredHistory } from './persistence.js';
import { buildSummaryFromEvents, filterEventsForCharacter } from './summaries.js';
import type { AquariumRuntimeView, PersistedRuntimeState } from './types.js';
import { RUNTIME_STATE_SCHEMA_VERSION } from './types.js';
import { isPresentDayPeriod, locationDisplayName, periodDisplayLabel } from './fallbackImage.js';

function withScheduleTimes(state: PersistedRuntimeState, snapshot: WorldSnapshot): PersistedRuntimeState {
  const inWorldDate = inWorldDateFromIso(state.clock.inWorldIso, snapshot.schedule);
  const weekKey = inWorldWeekKeyFromDate(inWorldDate);
  const dated = ensureDailyDevices(snapshot, state, inWorldDate, weekKey);
  const absences = pruneAbsenceHistory(dated.characterAbsences ?? [], dated.clock.inWorldIso);
  return {
    ...dated,
    characterAbsences: absences,
    operatingStatus: operatingStatusForClock(snapshot.schedule, dated.clock.inWorldIso).status,
  };
}

export function initRuntimeState(handoff: WorldLaunchHandoff): PersistedRuntimeState {
  const { snapshot } = handoff;
  const existing = loadRuntimeState(snapshot.worldId, snapshot.schedule);
  if (existing) return existing;

  const now = Date.now();
  const inWorldDate = inWorldDateFromIso(snapshot.clock.inWorldIso, snapshot.schedule);
  let state: PersistedRuntimeState = {
    schemaVersion: RUNTIME_STATE_SCHEMA_VERSION,
    worldId: snapshot.worldId,
    clock: { ...snapshot.clock },
    operatingStatus: snapshot.operatingStatus,
    activeDeviceIds: [],
    usedDevicesThisWeek: [],
    deviceWeekKey: inWorldWeekKeyFromDate(inWorldDate),
    currentInWorldDate: inWorldDate,
    historyEvents: [],
    summaries: [],
    eventObservations: [],
    lastVisitedAt: new Date(now).toISOString(),
    clockAnchorMs: now,
    characterAbsences: [],
  };
  state = withScheduleTimes(state, snapshot);
  saveRuntimeState(state);
  return state;
}

export function resumeRuntimeState(handoff: WorldLaunchHandoff, options?: {
  /** Default true. Pass false when the caller stamps the visit only while the world stays loaded. */
  stampVisit?: boolean;
}): {
  state: PersistedRuntimeState;
  showMissedPrompt: boolean;
  awayMs: number;
} {
  const { snapshot } = handoff;
  let state = loadRuntimeState(snapshot.worldId, snapshot.schedule) ?? initRuntimeState(handoff);
  const prevVisitIso = state.lastVisitedAt;
  const prevVisit = new Date(prevVisitIso).getTime();
  const nowMs = Date.now();
  const awayMs = nowMs - prevVisit;

  const synced = syncClockToNow(state.clock, state.clockAnchorMs, nowMs);
  state = {
    ...state,
    clock: synced.clock,
    clockAnchorMs: synced.anchorMs,
  };
  const historyNow = isPresentDayPeriod(snapshot.timePeriod)
    ? new Date(nowMs).toISOString()
    : state.clock.inWorldIso;
  state = withoutRetiredHistory(state, historyNow);
  state = tagMissedEventsAfterAbsence(state, awayMs, prevVisitIso);
  if (options?.stampVisit !== false) {
    state = { ...state, lastVisitedAt: new Date(nowMs).toISOString() };
  }
  state = withScheduleTimes(state, snapshot);
  const showMissed = shouldShowMissedPrompt(state, awayMs);
  saveRuntimeState(state);
  return { state, showMissedPrompt: showMissed, awayMs };
}

export function tickRuntimeState(
  state: PersistedRuntimeState,
  snapshot: WorldSnapshot,
  deltaMs: number,
): PersistedRuntimeState {
  if (deltaMs <= 0) return state;
  let next = {
    ...state,
    clock: advanceWorldClock(state.clock, deltaMs),
    clockAnchorMs: state.clockAnchorMs + deltaMs,
  };
  next = withScheduleTimes(next, snapshot);
  return next;
}

export function recordHistoryEvent(
  state: PersistedRuntimeState,
  event: HistoryEventRecord,
  userPresent = true,
): PersistedRuntimeState {
  if (isRetiredHistorySummary(event.summary)) return state;
  const prior = state.historyEvents[state.historyEvents.length - 1];
  if (
    prior
    && prior.summary === event.summary
    && prior.occurredAt === event.occurredAt
    && prior.actorIds.join('|') === event.actorIds.join('|')
  ) return state;
  let next = { ...state, historyEvents: [...state.historyEvents, event] };
  if (userPresent) next = markEventUserObserved(next, event.eventId);
  saveRuntimeState(next);
  return next;
}

export function dismissMissedPromptState(state: PersistedRuntimeState): PersistedRuntimeState {
  const next = acknowledgeMissedEvents(state);
  saveRuntimeState(next);
  return next;
}

export function buildRuntimeView(args: {
  handoff: WorldLaunchHandoff;
  state: PersistedRuntimeState;
  showMissedPrompt: boolean;
  focusedCharacterId: string | null;
  isNarrowViewport: boolean;
  catchingUp?: boolean;
}): AquariumRuntimeView {
  const {
    handoff, state, showMissedPrompt, focusedCharacterId, isNarrowViewport, catchingUp = false,
  } = args;
  const { snapshot } = handoff;
  const absentIds = activeAbsentCharacterIds(state.characterAbsences ?? [], state.clock.inWorldIso);
  const presentCount = handoff.roster.filter((n, index) => {
    if (absentIds.has(n.id)) return false;
    const character = snapshot.characters[index];
    return !character || isCharacterPresent(state.clock.inWorldIso, character.visitor, character.visits);
  }).length;
  const activeDevices = resolveActiveDevices(snapshot, state.activeDeviceIds);
  const locationSummary =
    buildSummaryFromEvents({
      worldId: snapshot.worldId,
      scope: 'location',
      events: state.historyEvents,
      prior: state.summaries.find((s) => s.scope === 'location') ?? null,
    }) ?? null;

  const missedEvents = missedHistoryEvents(state);
  const missedSummary = showMissedPrompt && missedEvents.length > 0
    ? buildSummaryFromEvents({
        worldId: snapshot.worldId,
        scope: 'missed_activity',
        events: missedEvents,
        prior: state.summaries.find((s) => s.scope === 'missed_activity') ?? null,
      })
    : null;

  return {
    handoff,
    snapshot,
    characters: handoff.characters,
    locationLabel: locationDisplayName(snapshot),
    periodLabel: periodDisplayLabel(snapshot),
    subtitle: formatAquariumSubtitle(state.clock, snapshot.schedule, presentCount, state.operatingStatus),
    clock: state.clock,
    operatingStatus: state.operatingStatus,
    activeDevices,
    historyEvents: state.historyEvents,
    summaries: state.summaries,
    lastVisitedAt: state.lastVisitedAt,
    showMissedPrompt: showMissedPrompt && missedEvents.length > 0,
    missedSummary,
    missedEvents,
    missedEventStory: state.missedEventStory ?? null,
    locationSummary,
    focusedCharacterId,
    isNarrowViewport,
    isEmptyRoster: handoff.roster.length === 0,
    characterAbsences: state.characterAbsences ?? [],
    catchingUp,
  };
}

export function recordCharacterAbsence(
  state: PersistedRuntimeState,
  record: import('../../runtime/characterAbsence.js').CharacterAbsenceRecord,
): PersistedRuntimeState {
  const next = {
    ...state,
    characterAbsences: pruneAbsenceHistory(
      [...(state.characterAbsences ?? []), record],
      state.clock.inWorldIso,
    ),
  };
  saveRuntimeState(next);
  return next;
}

export function characterHistorySummary(
  view: AquariumRuntimeView,
  characterId: string,
): SummaryRecord | null {
  const events = filterEventsForCharacter(view.historyEvents, characterId);
  return buildSummaryFromEvents({
    worldId: view.snapshot.worldId,
    scope: 'character',
    events,
    characterId,
    prior: view.summaries.find((s) => s.scope === 'character' && s.characterId === characterId) ?? null,
  });
}

export { formatWorldClock };

export function closedEpisodeMessage(): string {
  return WORLD_DOMAIN.defaultClosedMessage;
}

export function isLocationOpen(status: OperatingStatus): boolean {
  return status === 'open';
}
